"use client";

import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  SimpleGrid,
  Image,
  Icon,
  Flex,
  Tag,
} from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { LuCheck } from "react-icons/lu";
import {
  getPricingPlans,
  getPlanFeatureBullets,
  type PlanKey,
  type PricingPlan,
  type BillingPeriod,
} from "../data/pricingData";
import {
  formatPlanPriceLabel,
  getAnnualBillingDisplayParts,
} from "../_lib/catalogAmounts";
import { CatalogPriceSkeleton } from "./CatalogPriceSkeleton";
import { BiSolidOffer } from "react-icons/bi";

interface Props {
  billing: BillingPeriod;
  onSelect: (plan: PricingPlan) => void;
  amountByPriceId: Record<string, number>;
  isCatalogLoading: boolean;
  visiblePlanIds?: PlanKey[];
  currentPriceId?: string | null;
  compact?: boolean;
}

export default function PricingCards({
  billing,
  onSelect,
  amountByPriceId,
  isCatalogLoading,
  visiblePlanIds,
  currentPriceId,
  compact = false,
}: Props) {
  const t = useTranslations("pricing");
  const pricingPlans = getPricingPlans(t).filter((plan) =>
    visiblePlanIds?.length ? visiblePlanIds.includes(plan.id as PlanKey) : true,
  );
  const featureBulletsByPlan = getPlanFeatureBullets(t);

  const getDisplayPrice = (plan: PricingPlan) =>
    formatPlanPriceLabel(plan, billing, amountByPriceId, t);

  const getDisplayPeriod = (plan: PricingPlan) => {
    if (plan.isCustom) return t("common.onDemand");
    if (plan.priceMonthly === 0) return t("common.forever");
    return billing === "monthly" ? t("common.perMonth") : t("common.perYear");
  };

  const renderPlanPriceBlock = (plan: PricingPlan) => {
    if (isCatalogLoading && (plan.id === "pro" || plan.id === "ultimate")) {
      return <CatalogPriceSkeleton billing={billing} variant="card" />;
    }

    const annualParts =
      billing === "annual"
        ? getAnnualBillingDisplayParts(plan, amountByPriceId, t)
        : null;

    if (annualParts) {
      return (
        <VStack align="start" gap={1} h="6rem" py={6}>
          <HStack align="baseline" gap={1}>
            <Text
              textStyle={{ base: "3xl", md: "5xl" }}
              fontWeight="light"
              lineHeight="1"
              color="neutral.900"
              letterSpacing="-0.02em"
              fontFamily="mono"
            >
              {annualParts.headlineAmount}
            </Text>
            <Text textStyle="xs" color="neutral.700" pt={2}>
              {annualParts.headlineSuffix}
            </Text>
          </HStack>
          <Text textStyle="xs" color="neutral.700">
            {annualParts.subline}
          </Text>
        </VStack>
      );
    }

    return (
      <Flex align="baseline" gap={2} flexWrap="wrap" h="6rem" py={6}>
        <Text
          textStyle={{ base: "3xl", md: "5xl" }}
          fontWeight="light"
          lineHeight="1"
          color="neutral.900"
          letterSpacing="-0.02em"
          fontFamily="mono"
        >
          {getDisplayPrice(plan)}
        </Text>
        <Text textStyle="xs" color="neutral.700">
          {getDisplayPeriod(plan)}
        </Text>
      </Flex>
    );
  };

  const getButtonProps = (plan: PricingPlan) => {
    if (plan.popular) {
      return { colorPalette: "primary" as const, variant: "solid" as const };
    }
    if (plan.id === "free" || plan.isCustom) {
      return { colorPalette: "neutral" as const, variant: "outline" as const };
    }
    return { colorPalette: "primary" as const, variant: "outline" as const };
  };

  return (
    <SimpleGrid
      columns={{ base: 1, md: 2, xl: pricingPlans.length }}
      gap={{ base: 5, md: 6 }}
      alignItems="stretch"
    >
      {pricingPlans.map((plan) => {
        const isPopular = plan.popular;
        const isCurrent = currentPriceId === plan.pricingId[billing];
        const planBullets = featureBulletsByPlan[plan.id as PlanKey];

        return (
          <Box
            key={plan.id}
            role="article"
            aria-label={plan.name}
            rounded="3xl"
            p={{ base: 6, md: 7 }}
            position="relative"
            bg="white"
            boxShadow={isPopular ? "shadow-primary-xl" : "shadow-md"}
            border="0.5px solid"
            borderColor={isPopular ? "primary.200" : "neutral.200"}
            transform={
              isPopular && !compact ? { xl: "translateY(-4px)" } : undefined
            }
            zIndex={isPopular ? 1 : 0}
            _hover={{
              transform:
                isPopular && !compact
                  ? { xl: "translateY(-8px)" }
                  : "translateY(-4px)",
              boxShadow: isPopular ? undefined : "shadow-lg",
            }}
            transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
            display="flex"
            flexDirection="column"
            h="full"
          >
            {isPopular ? (
              <Tag.Root
                position="absolute"
                top="-4"
                left="50%"
                transform="translateX(-50%)"
                colorPalette="primary"
                variant="solid"
                size="xl"
                rounded="full"
                px={3}
                boxShadow="md"
              >
                <Tag.Label
                  display="flex"
                  alignItems="center"
                  gap={1}
                  textStyle="xs"
                  fontWeight="bold"
                >
                  <Icon as={BiSolidOffer} boxSize={4} />
                  {t("common.mostPopular")}
                </Tag.Label>
              </Tag.Root>
            ) : null}

            <VStack align="stretch" gap={6} flex="1">
              <VStack align="start" gap={3}>
                {plan.image ? (
                  <Image
                    src={plan.image}
                    alt=""
                    boxSize="100px"
                    w="fit-content"
                    objectFit="contain"
                    aria-hidden
                    // animation="spin 36s linear infinite"
                  />
                ) : null}
                <Box>
                  <Text
                    textStyle="xl"
                    fontWeight="semibold"
                    fontFamily="heading"
                    color="neutral.900"
                    letterSpacing="-0.02em"
                  >
                    {plan.name}
                  </Text>
                  <Text
                    color="neutral.500"
                    textStyle="sm"
                    mt={1}
                    lineHeight="tall"
                  >
                    {plan.description}
                  </Text>
                </Box>
              </VStack>

              {renderPlanPriceBlock(plan)}

              <Button
                {...getButtonProps(plan)}
                size="md"
                w="full"
                rounded="xl"
                onClick={() => onSelect(plan)}
                disabled={isCurrent}
              >
                {isCurrent ? t("common.currentPlan") : plan.cta}
              </Button>

              {!compact ? (
                <VStack
                  align="stretch"
                  gap={2}
                  flex="1"
                  pt={2}
                  borderTopWidth="1px"
                  borderColor="neutral.100"
                >
                  {planBullets.inheritLabel ? (
                    <Text
                      textStyle="xs"
                      color="neutral.400"
                      fontStyle="italic"
                      mb={1}
                    >
                      {planBullets.inheritLabel}
                    </Text>
                  ) : null}
                  {planBullets.bullets.map((bullet, idx) => (
                    <HStack key={idx} align="flex-start" gap={2}>
                      <Icon
                        as={LuCheck}
                        color="green.500"
                        boxSize={3.5}
                        mt="2px"
                        flexShrink={0}
                        aria-hidden
                      />
                      <Text
                        textStyle="sm"
                        color={bullet.highlight ? "primary.600" : "neutral.700"}
                        fontWeight={bullet.highlight ? "semibold" : "normal"}
                        lineHeight="short"
                      >
                        {bullet.label}
                      </Text>
                    </HStack>
                  ))}
                </VStack>
              ) : null}
            </VStack>
          </Box>
        );
      })}
    </SimpleGrid>
  );
}
